import { useEffect, useMemo } from 'react';
import { useTargetAudienceQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import {
  TargetAudienceRequest,
  TargetAudienceResponse,
} from 'store/api/apiTypes';

/**
 * useTargetAudience
 * Fetches Target Audience rule (information completeness) analysis for selected project.
 * Provides error modal integration & convenience derived fields.
 */
export const useTargetAudience = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);
  const queryParams = useMemo<TargetAudienceRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: targetAudience,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useTargetAudienceQuery(queryParams as TargetAudienceRequest, {
    skip: !queryParams,
    refetchOnMountOrArgChange: true,
  });

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) {
      handleApiError(error);
    }
  }, [error, handleApiError]);

  usePollingOnError(queryParams, error, refetch, targetAudience);

  const companyAnalysis = targetAudience?.company_analysis;

  return {
    targetAudience: targetAudience as TargetAudienceResponse | undefined,
    companyAnalysis,
    productAnalyses: targetAudience?.product_analyses || {},
    isLoading,
    isFetching,
    error,
    refetchTargetAudience: refetch,
    showTargetAudienceErrorModal: showErrorModal,
    handleTargetAudienceRetry: handleRetry,
    hideTargetAudienceErrorModal: hideErrorModal,
    hasTargetAudience: !!targetAudience,
    companyDomain: companyAnalysis?.domain,
    finalScore: companyAnalysis?.final_score,
    overallAssessment: companyAnalysis?.overall_assessment,
  };
};
